/**
 * features/teachers/TeacherHrPay.tsx
 * ------------------------------------------------------------------
 * The "HR & Pay" tab on the academic teacher profile (Phase 2 —
 * employee unification). Surfaces the linked HR staff record and its
 * payroll profile WITHOUT merging the two domains — the bridge stays
 * HrStaff.linkedTeacherId.
 *
 * Link resolution: by linkedTeacherId first, then a shared-email
 * fallback that an HR user can confirm into a permanent link; if
 * neither, an HR user can search and link a staff record manually.
 *
 * Read-only summary here (editing stays in the HR module). Pay figures
 * are gated on canAccessPayroll, so an HR officer without payroll sees
 * the employment details but not the salary.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import {
  Link2,
  Building2,
  Banknote,
  CheckCircle2,
  Search,
} from 'lucide-react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { hr } from '@/features/hr/hrWrite';
import { SoftCard, StatChip, AccentButton } from '@/components/ui/glass';
import type { HrStaff } from '@/lib/hr';
import type { HrPayrollProfile } from '@/lib/hrPayroll';

const fmtPula = (n: number) =>
  'P' +
  (n || 0).toLocaleString('en-BW', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function TeacherHrPay({
  teacherId,
  teacherEmail,
  teacherName,
}: {
  teacherId: string;
  teacherEmail?: string;
  teacherName?: string;
}) {
  const { permissions } = useAuth();
  const [staff, setStaff] = useState<HrStaff[]>([]);
  const [profiles, setProfiles] = useState<HrPayrollProfile[]>([]);
  const [ready, setReady] = useState(false);
  const [linking, setLinking] = useState(false);
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let a = false;
    let b = false;
    const settle = () => {
      if (a && b) setReady(true);
    };
    const u1 = onSnapshot(collection(db, 'hrStaff'), (s) => {
      setStaff(s.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as HrStaff));
      a = true;
      settle();
    });
    const u2 = onSnapshot(collection(db, 'hrPayrollProfile'), (s) => {
      setProfiles(
        s.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as HrPayrollProfile),
      );
      b = true;
      settle();
    });
    return () => {
      u1();
      u2();
    };
  }, []);

  const linked = useMemo(
    () => staff.find((s) => s.linkedTeacherId === teacherId),
    [staff, teacherId],
  );
  const emailMatch = useMemo(() => {
    if (!teacherEmail) return undefined;
    const e = teacherEmail.trim().toLowerCase();
    return staff.find((s) => s.email && s.email.trim().toLowerCase() === e);
  }, [staff, teacherEmail]);
  const profile = useMemo(
    () => (linked ? profiles.find((p) => p.staffId === linked.id) : undefined),
    [profiles, linked],
  );

  const canLink = permissions.canAccessHR; // linking is an HR action
  const canPay = permissions.canAccessPayroll;

  async function linkTo(staffId: string) {
    if (!canLink || linking) return;
    setLinking(true);
    setMsg(null);
    try {
      await hr.staff.update(staffId, { linkedTeacherId: teacherId });
      setMsg('Linked.');
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setLinking(false);
    }
  }
  async function unlink(staffId: string) {
    if (!canLink || linking) return;
    setLinking(true);
    setMsg(null);
    try {
      await hr.staff.update(staffId, { linkedTeacherId: null });
      setMsg('Unlinked.');
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setLinking(false);
    }
  }

  if (!ready) {
    return (
      <div className="py-10 text-center text-sm text-gray-500">
        Loading HR record…
      </div>
    );
  }

  // ── Linked ────────────────────────────────────────────────────
  if (linked) {
    const gross =
      (profile?.basicSalary ?? 0) +
      (profile?.allowances?.reduce((s, a) => s + (a.amount || 0), 0) ?? 0);
    return (
      <div className="space-y-4">
        <SoftCard>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <Building2 size={18} className="text-primary" />
              <div>
                <p className="font-semibold">
                  {linked.firstName} {linked.lastName}
                </p>
                <p className="text-xs text-gray-500">
                  Employee #{linked.employeeNo} · {linked.jobTitle}
                  {linked.department ? ` · ${linked.department}` : ''}
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
              <CheckCircle2 size={14} /> Linked
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <StatChip label="Status" value={linked.status} />
            <StatChip label="Residency" value={linked.residency} />
            <StatChip
              label="Start date"
              value={linked.startDate ? new Date(linked.startDate).toLocaleDateString() : '—'}
            />
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Employment details are managed in HR → Staff Directory.
          </p>
        </SoftCard>

        {canPay ? (
          <SoftCard>
            <div className="flex items-center gap-2 mb-2">
              <Banknote size={18} className="text-[var(--accent-link,#0d9488)]" />
              <p className="font-semibold">Pay</p>
            </div>
            {profile ? (
              <>
                <div className="flex flex-wrap gap-2">
                  <StatChip label="Basic salary" value={fmtPula(profile.basicSalary)} />
                  {(profile.allowances ?? []).map((a, i) => (
                    <StatChip key={i} label={a.type} value={fmtPula(a.amount)} />
                  ))}
                  <StatChip label="Gross (monthly)" value={fmtPula(gross)} />
                </div>
                <p className="mt-3 text-xs text-gray-500">
                  Set on the staff member's Payroll tab in HR. Flows into each pay run automatically.
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500">
                No payroll profile yet. Add basic salary &amp; allowances on the staff member's Payroll tab in HR.
              </p>
            )}
          </SoftCard>
        ) : (
          <SoftCard>
            <p className="text-sm text-gray-500">
              Pay details are restricted to payroll roles.
            </p>
          </SoftCard>
        )}

        {canLink && (
          <div className="text-right">
            <button
              onClick={() => unlink(linked.id)}
              disabled={linking}
              className="text-xs text-gray-500 hover:text-red-600 underline"
            >
              Unlink this HR record
            </button>
          </div>
        )}
        {msg && <p className="text-xs text-gray-500 text-right">{msg}</p>}
      </div>
    );
  }

  // ── Email match, not yet linked ───────────────────────────────
  if (emailMatch) {
    return (
      <SoftCard>
        <div className="flex items-center gap-2 mb-2">
          <Link2 size={18} className="text-amber-500" />
          <p className="font-semibold">Possible HR record found</p>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          An HR staff record shares this teacher's email:{' '}
          <span className="font-medium">
            {emailMatch.firstName} {emailMatch.lastName}
          </span>{' '}
          (#{emailMatch.employeeNo}, {emailMatch.jobTitle}).
        </p>
        {canLink ? (
          <div className="mt-3">
            <AccentButton onClick={() => linkTo(emailMatch.id)} disabled={linking}>
              {linking ? 'Linking…' : 'Link these records'}
            </AccentButton>
          </div>
        ) : (
          <p className="mt-2 text-xs text-gray-500">
            An HR administrator can confirm this link.
          </p>
        )}
        {msg && <p className="mt-2 text-xs text-gray-500">{msg}</p>}
      </SoftCard>
    );
  }

  // ── No match — manual link (HR users) ─────────────────────────
  const q = search.trim().toLowerCase();
  const candidates = q
    ? staff
        .filter(
          (s) =>
            !s.linkedTeacherId &&
            (`${s.firstName} ${s.lastName}`.toLowerCase().includes(q) ||
              s.employeeNo?.toLowerCase().includes(q) ||
              s.jobTitle?.toLowerCase().includes(q)),
        )
        .slice(0, 8)
    : [];
  return (
    <SoftCard>
      <div className="flex items-center gap-2 mb-1">
        <Link2 size={18} className="text-gray-400" />
        <p className="font-semibold">No HR record linked</p>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {teacherName ? `${teacherName} isn't` : "This teacher isn't"} linked to an HR staff
        record yet. Once linked, their employment and pay appear here and they're included in payroll.
      </p>
      {canLink ? (
        <div className="mt-3">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search staff by name, number or job title…"
              className="w-full pl-7 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm"
            />
          </div>
          {candidates.length > 0 && (
            <div className="mt-2 space-y-1">
              {candidates.map((s) => (
                <button
                  key={s.id}
                  onClick={() => linkTo(s.id)}
                  disabled={linking}
                  className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
                >
                  <span className="font-medium">
                    {s.firstName} {s.lastName}
                  </span>
                  <span className="text-xs text-gray-500">
                    {' '}
                    · #{s.employeeNo} · {s.jobTitle}
                  </span>
                </button>
              ))}
            </div>
          )}
          {q && candidates.length === 0 && (
            <p className="mt-2 text-xs text-gray-500">
              No unlinked staff match "{search}". Create the staff record in HR → Staff Directory first.
            </p>
          )}
        </div>
      ) : (
        <p className="mt-2 text-xs text-gray-500">
          An HR administrator can link this teacher to a staff record.
        </p>
      )}
      {msg && <p className="mt-2 text-xs text-gray-500">{msg}</p>}
    </SoftCard>
  );
}
